"use client";

import { useState } from "react";
import type { FormEvent } from "react";
import {
  captureMatchingContact,
  captureReadingContact,
  type ContactCaptureBody,
} from "@/lib/api/chat";
import { ApiError } from "@/lib/api/_fetch";
import { RetryExhaustedError } from "@/lib/api/_retry";
import { trackEvent } from "@/lib/analytics-events";
import { getCheckoutPrefill, rememberCheckoutPrefillFromContact } from "@/lib/checkout-prefill";
import { CAPTURE_POINTS } from "@/lib/contact-capture-points";
import { MARKETING_CONSENT_COPY_VERSION } from "@/lib/marketing-consent";
import { HINDI_CONSENT_COPY } from "@/lib/marketing-consent-copy-hi";
import { normalizePhoneE164 } from "@/lib/phone-e164";
import { PhoneInputE164 } from "@/components/PhoneInputE164";
import { useImpressionOnce } from "@/hooks/useImpressionOnce";
import { CompactValueMomentCapture } from "./ValueMomentContactCapture.compact";
import { useSharedValueMomentOptIn, valueMomentOptInKey } from "./ValueMomentOptInContext";

type CaptureTarget = { type: "reading" | "match"; id: string };
/**
 * A reading that does not exist yet and is created only when the seeker taps
 * the opt-in (e.g. /dasamsa, whose D10 endpoint mints no reading). `pendingKey`
 * stands in for the id until then (impression dedupe, shared opt-in key, DOM
 * ids) and should change whenever the subject (birth details) changes.
 * `resolveId` runs at most once per successful resolve: a phone retry after a
 * 400 reuses the same reading, and a rejected resolve records no consent.
 */
export type LazyReadingTarget = {
  type: "reading";
  pendingKey: string;
  resolveId: () => Promise<string>;
};
type SubmitState = "idle" | "saving" | "saved" | "error" | "invalid";

/**
 * `variant="compact"` is the dense card under the kundali chart. It shows the
 * same audited consent copy (heading + body, verbatim) and sends the same
 * payload through the same submit path; only the layout differs, so the copy
 * version still names exactly what the seeker read (rule 83).
 */
export function ValueMomentContactCapture({
  target,
  language,
  placement,
  variant = "full",
}: {
  target: CaptureTarget | LazyReadingTarget;
  language: string;
  placement: string;
  variant?: "full" | "compact";
}) {
  const hi = language === "hi";
  const copy = contactCopy(target.type, hi);
  const optIn = useValueMomentOptIn(target, placement);
  const { asksForPhone, needsPhone, phoneInput, state } = optIn;

  if (optIn.dismissed) return null;
  if (variant === "compact") {
    return <CompactValueMomentCapture copy={copy} optIn={optIn} targetId={optIn.targetKey} />;
  }
  if (state === "saved") return <ConsentConfirmation copy={copy.success} />;

  return (
    <div
      className="rounded-lg border border-ish-border bg-ish-bg p-4"
      data-testid="value-moment-contact-capture"
    >
      <p className="text-body font-semibold text-ish-dark">{copy.heading}</p>
      <p className="mt-1 text-caption leading-relaxed text-ish-text-muted">{copy.body}</p>
      {needsPhone ? (
        <p className="mt-1 text-caption font-medium text-ish-dark">{copy.needsPhone}</p>
      ) : null}
      <form
        translate="no"
        onSubmit={optIn.handleSubmit}
        className="mt-3 flex flex-col gap-2 sm:flex-row"
      >
        {asksForPhone ? (
          <PhoneInputE164
            value={phoneInput}
            onChange={optIn.changePhone}
            disabled={state === "saving"}
            id={`vm-phone-${optIn.targetKey}`}
            name="phone"
            placeholder={copy.placeholder}
            countryLabel={copy.countryLabel}
            wrapperClassName="flex min-w-0 flex-1 items-stretch"
            className="min-w-0 flex-1 rounded-r px-3 py-2 text-body-small outline-none"
          />
        ) : null}
        <button
          type="submit"
          disabled={state === "saving" || (asksForPhone && !phoneInput.trim())}
          className="rounded px-4 py-2 text-body-small font-semibold text-white disabled:opacity-50"
          style={{ background: "var(--ish-accent)" }}
        >
          {state === "saving" ? copy.saving : copy.cta}
        </button>
        <button
          type="button"
          onClick={optIn.dismiss}
          className="rounded px-3 py-2 text-caption font-medium text-ish-text-muted"
        >
          {copy.skip}
        </button>
      </form>
      {state === "invalid" || state === "error" ? (
        <p className="mt-2 text-[11px] text-red-700">
          {state === "invalid" ? copy.invalid : copy.error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * State + the one explicit consent action shared by both variants. When a
 * `ValueMomentOptInProvider` is mounted, an opt-in through any card for this
 * target puts every card for it into the confirmed state, so no card asks twice.
 *
 * Re-permission (owner decision 2026-10-25): the confirmed state comes ONLY
 * from an opt-in made in this page session, never from a stored consent flag.
 * Most stored flags (06-14..07-15 pre-checked / required-phone cohorts) are not
 * provable consent, so a seeker who "has" one must be asked again here; their
 * tap records the audited v3 opt-in that makes their consent strong. Never
 * seed `saved` from the contact's stored consent flags.
 */
function useValueMomentOptIn(target: CaptureTarget | LazyReadingTarget, placement: string) {
  // Reading targets are one-tap: the phone is already on the reading row
  // (mandatory form capture, rule 69) or in the checkout prefill. The input
  // is revealed only when the backend proves no phone is known (legacy
  // readings) — `needsPhone` — so the common path stays frictionless.
  const [needsPhone, setNeedsPhone] = useState(false);
  const asksForPhone = target.type === "match" || needsPhone;
  const [phoneInput, setPhoneInput] = useState("");
  const [localState, setState] = useState<SubmitState>("idle");
  const [dismissed, setDismissed] = useState(false);
  // Lazy subject: the reading id once the seeker's tap created it.
  const [resolvedId, setResolvedId] = useState<string | null>(null);
  const knownId = "id" in target ? target.id : resolvedId;
  const targetKey = "id" in target ? target.id : target.pendingKey;
  const shared = useSharedValueMomentOptIn(
    valueMomentOptInKey({ type: target.type, id: targetKey })
  );
  const state: SubmitState = shared?.optedIn ? "saved" : localState;

  // A card that is already confirmed is not asking, so it records no impression.
  // A lazy subject has no reading yet, so its impression carries no reading id.
  const impressionKey =
    state === "saved" ? null : `contact_capture|${target.type}|${targetKey}|${placement}`;
  useImpressionOnce(impressionKey, () => {
    trackEvent("contact_capture_shown", {
      ...(knownId ? { [`${target.type}_id`]: knownId } : {}),
      value_surface: target.type,
      placement,
      source_path: currentPath(),
    });
  });

  // The reading id to attach consent to. A lazy subject's reading is created
  // here, once; a phone retry after a 400 reuses it. A failed create is
  // recorded (contact_capture_failed) so it is a measurable rate, not silence.
  async function subjectId(): Promise<string | null> {
    if (knownId) return knownId;
    const resolved = await resolveLazyId(target);
    if ("error" in resolved) {
      trackEvent("contact_capture_failed", {
        value_surface: target.type,
        placement,
        reason: READING_CREATE_FAILED,
        error_kind: resolved.error,
        source_path: currentPath(),
      });
      return null;
    }
    setResolvedId(resolved.id);
    return resolved.id;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const rememberedPhone = getCheckoutPrefill().contact ?? "";
    const phone = normalizePhoneE164(asksForPhone ? phoneInput : rememberedPhone);
    if (asksForPhone && !phone) {
      setState("invalid");
      return;
    }
    setState("saving");
    // A lazy subject's reading is created only now, on the explicit tap. Its
    // failure is not the contact 400 below: no reading, no consent, error state.
    const id = await subjectId();
    if (!id) {
      setState("error");
      return;
    }
    const subject: CaptureTarget = { type: target.type, id };
    try {
      await captureValueMomentContact(subject, {
        ...(phone ? { phone } : {}),
        consent_marketing: true,
        consent_copy_version: MARKETING_CONSENT_COPY_VERSION,
        consent_placement: placement,
      });
      if (phone) rememberCheckoutPrefillFromContact({ phone });
      trackEvent("contact_captured", {
        [`${target.type}_id`]: id,
        value_surface: target.type,
        type: "phone",
        placement,
        capture_point: CAPTURE_POINTS.VALUE_MOMENT,
        source_path: currentPath(),
        consent_marketing: true,
        consent_copy_version: MARKETING_CONSENT_COPY_VERSION,
      });
      setState("saved");
      shared?.markOptedIn();
    } catch (err) {
      // A 400 on a one-tap reading opt-in means no phone is known anywhere
      // (backend: "WhatsApp opt-in requires a phone" / "email or phone is
      // required" — legacy readings without a captured contact). Reveal the
      // phone input instead of dead-ending, so the seeker can still opt in.
      if (
        target.type === "reading" &&
        !asksForPhone &&
        err instanceof ApiError &&
        err.status === 400
      ) {
        setNeedsPhone(true);
        setState("idle");
        return;
      }
      setState("error");
    }
  }

  return {
    state,
    targetKey,
    needsPhone,
    asksForPhone,
    phoneInput,
    dismissed,
    handleSubmit,
    changePhone: (next: string) => {
      setPhoneInput(next);
      setState("idle");
    },
    dismiss: () => setDismissed(true),
  };
}

/**
 * `reason` on contact_capture_failed when the lazy reading could not be created.
 * @internal
 */
export const READING_CREATE_FAILED = "reading_create_failed";

/**
 * Coarse, PII-free class of a failed lazy create: the HTTP status for an API
 * error, never its message (field errors can echo the seeker's birth details).
 */
type LazyResolveError = `http_${number}` | "retries_exhausted" | "no_reading_id" | "unexpected";

/** Creates a lazy subject's reading, or says why it could not. */
async function resolveLazyId(
  target: CaptureTarget | LazyReadingTarget
): Promise<{ id: string } | { error: LazyResolveError }> {
  if (!("resolveId" in target)) return { error: "no_reading_id" };
  try {
    const id = await target.resolveId();
    return id ? { id } : { error: "no_reading_id" };
  } catch (err) {
    if (err instanceof ApiError) return { error: `http_${err.status}` };
    if (err instanceof RetryExhaustedError) return { error: "retries_exhausted" };
    return { error: "unexpected" };
  }
}

function ConsentConfirmation({ copy }: { copy: string }) {
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" role="status">
      <p className="text-body-small font-medium text-emerald-800">{copy}</p>
    </div>
  );
}

async function captureValueMomentContact(target: CaptureTarget, body: ContactCaptureBody) {
  if (target.type === "match") return captureMatchingContact(target.id, body);
  return captureReadingContact(target.id, body);
}

/**
 * Relationship/continuity framing (copy version jyotish_updates_v3): the AI
 * Jyotishi REMEMBERS this chart and follows up — not a generic "updates"
 * newsletter ask. Rules 21/22: Indian noun + English verb, verified vocabulary
 * (kundali, milan, rashifal, graha, upay), no therapy language.
 *
 * v3 names WhatsApp AND email because a daily loop has to run on a FREE channel
 * (WhatsApp is ~Rs0.78/msg). The channels registered are derived from this
 * version by contact_capture._CONSENT_COPY_CHANNELS, so this wording and that
 * mapping must always agree — the version records exactly what was agreed.
 * Any wording change here requires bumping
 * MARKETING_CONSENT_COPY_VERSION (rule 83 consent audit). The Hindi rendering
 * is the same agreement in Devanagari (lib/marketing-consent-copy-hi.ts).
 */
function contactCopy(targetType: CaptureTarget["type"], hi: boolean) {
  const match = targetType === "match";
  if (hi) {
    const copy = HINDI_CONSENT_COPY.hi;
    return {
      heading: match ? copy.matchHeading : copy.readingHeading,
      body: match ? copy.matchBody : copy.readingBody,
      cta: copy.cta,
      saving: copy.saving,
      skip: copy.skip,
      placeholder: copy.placeholder,
      countryLabel: copy.countryLabel,
      invalid: copy.invalid,
      needsPhone: copy.needsPhone,
      error: copy.error,
      success: copy.success,
    };
  }
  return {
    heading: match
      ? "Aapka AI Jyotishi will remember this milan"
      : "Aapka AI Jyotishi will remember this kundali",
    body: match
      ? "Get updates for this match on WhatsApp and email, with upay suggestions when the time is right. Reply STOP anytime."
      : "Get rashifal and graha updates for this chart on WhatsApp and email, with upay suggestions when the time is right. Reply STOP anytime.",
    cta: "Yes, send them",
    saving: "Saving...",
    skip: "Not now",
    placeholder: "Mobile number",
    countryLabel: "Country code",
    invalid: "Enter a valid mobile number.",
    needsPhone: "Add your mobile number to turn on WhatsApp updates.",
    error: "We could not save your permission. Please try again.",
    success: "Updates are on. Reply STOP anytime to turn them off.",
  };
}

function currentPath(): string {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
}
